// コメントスレッド（comment-thread オブジェクト）の純粋ロジック。
//
// サーバ（op-applier）とクライアント（canvas-doc-store の applyOpToObjects）の双方が
// この同じ関数を使い、コメント op を決定論的に適用する。これにより楽観反映・サーバ反映・
// 他参加者反映が完全に一致する（既存の applyOpToObjects / applyOpToCanvasTable の対称性方針）。

import { VIEWER_AVATAR_IMAGE_URL_MAX_LENGTH } from "../realtime/viewer";
import type { CanvasObject, CommentMessage, CommentThreadCanvasObject } from "./canvas-object";

/** 1 メッセージ本文の最大長（付箋より短く、コメント用途として十分な長さ）。 */
export const COMMENT_BODY_MAX_LENGTH = 2048;
/** 1 スレッドあたりのメッセージ最大件数（DynamoDB アイテムの肥大化を防ぐ上限）。 */
export const COMMENT_MESSAGES_MAX = 100;
/** メッセージに保存するアバター URL スナップショットの最大長（viewer のアバター上限に合わせる）。 */
export const COMMENT_AVATAR_MAX_LENGTH = VIEWER_AVATAR_IMAGE_URL_MAX_LENGTH;
/** 1 メッセージに添付できる画像の最大枚数（DynamoDB アイテムの肥大化を防ぐ上限）。 */
export const COMMENT_IMAGES_MAX = 4;
/**
 * メッセージに保存する画像 URL の最大長。画像は添付基盤（attachments/<canvasId>/... へ昇格した
 * 公開 URL）を指す前提で、キー構成（プレフィックス + canvasId + objectId + タイムスタンプ +
 * 拡張子）に十分な余裕を持たせる。
 */
export const COMMENT_IMAGE_URL_MAX_LENGTH = 600;

/** comment-thread オブジェクトかどうかの型ガード。 */
export function isCommentThread(object: CanvasObject): object is CommentThreadCanvasObject {
  return object.type === "comment-thread";
}

/**
 * コメント添付画像 URL が、この環境の添付公開ベース URL 配下に属するかを返す（サーバ権威検証用）。
 * API のスキーマ（CommentImageUrlSchema）が行う「スキーム・正規化後 /attachments/ パス」の形式検証に
 * 加え、絶対 URL のホスト一致をここで縛り、外部ドメインの /attachments/ パス偽装
 * （例: https://attacker.example/attachments/x.png）を弾く。WebSocket op-applier と REST/MCP の
 * 生成経路の双方が、この同じ関数を正本として使う。
 * - 同一オリジン相対（"/attachments/" 配下）はホスト概念が無く、形式検証で十分なため常に許可する。
 * - 絶対 URL は、baseUrl が設定済みで、かつ `${baseUrl}/attachments/` を前方一致する場合のみ許可する
 *   （baseUrl 未設定の環境は公開 URL を発行できず、正規クライアントは絶対 URL を送らないため一律不許可）。
 */
export function isCommentImageUrlAllowedForBase(
  url: string,
  attachmentsPublicBaseUrl: string | undefined
): boolean {
  // 同一オリジン相対はスキーマの正規化検証に委ねる（ホスト縛りは対象外）。
  if (url.startsWith("/")) return true;
  if (attachmentsPublicBaseUrl === undefined || attachmentsPublicBaseUrl.length === 0) return false;
  const allowedPrefix = `${attachmentsPublicBaseUrl.replace(/\/$/, "")}/attachments/`;
  return url.startsWith(allowedPrefix);
}

/**
 * コメントスレッド（ピン）から `locked` を取り除く。comment-thread 以外はそのまま返す。
 *
 * ピンはロック（固定）の概念を持たない: 通常オブジェクトの選択・右クリックメニューの対象外で、
 * transform も「位置移動（x / y）のみ」に限定されているため、いったん `locked: true` が保存されると
 * ロックを解除する手段が無い。その状態でコメント op（返信・編集・削除）まで拒否すると、スレッドを
 * 二度と操作できないデッドロックになる。
 *
 * そこで「locked な comment-thread は永続化しない」ことをサーバ権威の不変条件にし、作成経路
 * （WebSocket の object.create / pen.stroke.commit と REST・MCP の create）で必ず落とす。これにより
 * コメント op 側にロック判定を持ち込まずに、細工クライアントが locked 付きスレッドを作って
 * 「ロック中なのに変更できるオブジェクト」を成立させることも防げる。
 */
export function stripCommentThreadLock(object: CanvasObject): CanvasObject {
  if (object.type !== "comment-thread") return object;
  return withoutThreadLock(object);
}

/**
 * コメントスレッドから `locked` を落とす内部ヘルパー（型を保ったまま除去する）。
 * 作成経路の {@link stripCommentThreadLock} に加えて、コメント op の適用結果にも通し、
 * 過去に保存されてしまった `locked: true` のスレッドを更新のたびに自己修復する。
 */
function withoutThreadLock(thread: CommentThreadCanvasObject): CommentThreadCanvasObject {
  if (thread.locked === undefined) return thread;
  const { locked: _ignoredLocked, ...rest } = thread;
  return rest;
}

/** コメント op 適用時に共通して上書きするメタ情報（最終更新時刻・最終更新者）。 */
export interface CommentApplyMeta {
  /** 最終更新時刻（ISO8601）。クライアント・サーバで同一にするため op.clientCreatedAt を渡す。 */
  readonly at: string;
  /** 最終更新者の userId（op.actorUserId）。 */
  readonly by: string;
}

/**
 * スレッドへメッセージを 1 件追加した次状態を返す。
 * 上限件数（COMMENT_MESSAGES_MAX）を超える追加は元のスレッドをそのまま返す（呼び出し側で事前検証する想定）。
 */
export function applyCommentAdd(
  thread: CommentThreadCanvasObject,
  message: CommentMessage,
  meta: CommentApplyMeta
): CommentThreadCanvasObject {
  if (thread.messages.length >= COMMENT_MESSAGES_MAX) return thread;
  // 同一 ID のメッセージが既にある場合は重複追加しない（op 再配信・楽観 ack の二重適用対策）。
  if (thread.messages.some((m) => m.id === message.id)) return thread;
  // 返り値からも locked を落とす（過去に保存された locked 付きスレッドの自己修復。理由は
  // stripCommentThreadLock 参照。修復しないと「コメントは更新できるのに移動・削除だけ拒否される」
  // 半壊状態が固定化してしまう）。
  return withoutThreadLock({
    ...thread,
    messages: [...thread.messages, message],
    updatedAt: meta.at,
    updatedBy: meta.by,
  });
}

/**
 * スレッド内の 1 メッセージ本文を編集した次状態を返す。
 * 対象 ID が無ければ元のスレッドをそのまま返す。
 */
export function applyCommentUpdate(
  thread: CommentThreadCanvasObject,
  messageId: string,
  body: string,
  meta: CommentApplyMeta
): CommentThreadCanvasObject {
  let changed = false;
  const messages = thread.messages.map((m) => {
    if (m.id !== messageId) return m;
    changed = true;
    return { ...m, body, updatedAt: meta.at, editedAt: meta.at };
  });
  if (!changed) return thread;
  // applyCommentAdd と同じく locked を落として自己修復する。
  return withoutThreadLock({ ...thread, messages, updatedAt: meta.at, updatedBy: meta.by });
}

/**
 * スレッドから 1 メッセージを削除（ハード削除）した次状態を返す。
 * 削除後にメッセージが 0 件になった場合は null を返す（呼び出し側でオブジェクトごと削除する合図）。
 * 対象 ID が無ければ元のスレッドをそのまま返す。
 */
export function applyCommentDelete(
  thread: CommentThreadCanvasObject,
  messageId: string,
  meta: CommentApplyMeta
): CommentThreadCanvasObject | null {
  if (!thread.messages.some((m) => m.id === messageId)) return thread;
  const messages = thread.messages.filter((m) => m.id !== messageId);
  if (messages.length === 0) return null;
  // applyCommentAdd と同じく locked を落として自己修復する。
  return withoutThreadLock({ ...thread, messages, updatedAt: meta.at, updatedBy: meta.by });
}

/**
 * スレッド全体を削除できるかの権限判定（クライアント UI のボタン表示・右クリック可否と、
 * サーバ op-applier の object.delete 受理判定の双方が、この同じ関数を唯一の正本として使う）。
 * 管理者、または全メッセージを自分が投稿している場合のみ true。
 * これにより「他の利用者の返信を含むスレッドを起点投稿者が一括削除して権限を迂回する」ことを防ぐ
 * （メッセージ単位の削除権限＝投稿者本人または管理者、と整合する）。最終判定は常にサーバが行う。
 */
export function canDeleteCommentThread(
  thread: CommentThreadCanvasObject,
  userId: string,
  isAdmin: boolean
): boolean {
  if (isAdmin) return true;
  // 空スレッド（メッセージ 0 件）は「最後の 1 件削除でスレッドごと消える」不変条件（applyCommentDelete が
  // null を返しオブジェクトごと削除）により通常は到達しないが、Array.every は空配列で true を返すため、
  // 万一空スレッドが残った場合（破損データ・競合・将来の経路追加等）に非投稿者へ削除権限が広がってしまう。
  // 正本ガードがこの不変条件へ暗黙に依存しないよう、空配列は明示的に弾く（権限を広げない安全側に倒す）。
  return thread.messages.length > 0 && thread.messages.every((message) => message.authorId === userId);
}

/** ピンに重ねて表示する参加者（投稿者）の表示用情報。 */
export interface CommentParticipant {
  readonly authorId: string;
  readonly authorName: string;
  readonly authorAvatar?: string | undefined;
}

/**
 * スレッドの参加者一覧を「初出順・重複排除」で返す（ピンのアバター重ね表示用）。
 * 同一投稿者の表示名・アバターは最後のメッセージのスナップショット（=最新）を採用する。
 */
export function commentThreadParticipants(
  thread: CommentThreadCanvasObject
): readonly CommentParticipant[] {
  const order: string[] = [];
  const byId = new Map<string, CommentParticipant>();
  for (const message of thread.messages) {
    if (!byId.has(message.authorId)) order.push(message.authorId);
    byId.set(message.authorId, {
      authorId: message.authorId,
      authorName: message.authorName,
      authorAvatar: message.authorAvatar,
    });
  }
  return order.map((id) => byId.get(id)!).filter((p): p is CommentParticipant => p !== undefined);
}
